/**
 * Dashboard Statistics Component
 *
 * Manages different views of dashboard statistics including registrations,
 * client activity, and user activity with dynamic date ranges.
 */
export default function dashboardStats() {
    return {
        // Current active view
        currentView: {
            key: 'registrations',
            label: 'Registrations',
            title: 'Registrations'
        },

        // Available stat views
        availableViews: [
            {
                key: 'registrations',
                label: 'Registrations',
                title: 'Registrations',
                description: 'Client registration statistics'
            },
            {
                key: 'intakes',
                label: 'Intakes',
                title: 'Intakes',
                description: 'Client interaction and service statistics'
            },
            {
                key: 'enrollments',
                label: 'Enrollments',
                title: 'Enrollments',
                description: 'System usage and user engagement metrics'
            }
        ],

        // Show/hide view menu
        showViewMenu: false,

        // Date range for statistics
        dateRange: 'week', // week, month, quarter, year

        // Statistics data
        stats: {
            registrations: {
                overall: 0,
                today: 0,
                thisWeek: 0,
                thisMonth: 0,
                thisYear: 0
            },
            clientActivity: {
                profileViews: 0,
                caseNotes: 0,
                assessments: 0,
                documents: 0
            },
            userActivity: {
                activeSessions: 0,
                loginsToday: 0,
                pageViews: 0,
                idleExtensions: 0
            }
        },

        // Loading states
        loading: {
            registrations: false,
            clientActivity: false,
            userActivity: false
        },

        /**
         * Initialize the component
         */
        init() {
            // Load initial stats for the default view
            this.loadStatsForView(this.currentView.key);
        },

        /**
         * Switch to a different statistics view
         */
        switchView(viewKey) {
            const view = this.availableViews.find(v => v.key === viewKey);
            if (view) {
                this.currentView = { ...view };
                this.showViewMenu = false;
                this.loadStatsForView(viewKey);
            }
        },

        /**
         * Load statistics for the specified view
         */
        async loadStatsForView(viewKey) {
            this.loading[viewKey.replace('-', '')] = true;

            try {
                switch (viewKey) {
                    case 'registrations':
                        await this.loadRegistrationStats();
                        break;
                    case 'intakes':
                        await this.loadIntakeMetrics();
                        break;
                    case 'user-activity':
                        await this.loadUserActivityStats();
                        break;
                }
            } catch (error) {
                console.error(`Error loading ${viewKey} stats:`, error);
                this.handleStatsError(viewKey);
            } finally {
                this.loading[viewKey.replace('-', '')] = false;
            }
        },

        /**
         * Load registration statistics
         * For now, this uses the existing PHP data, but can be enhanced with AJAX calls
         */
        async loadRegistrationStats() {
            // Since registration stats are already loaded from PHP, we can keep using them
            // In the future, this could make an AJAX call to get real-time data
            return Promise.resolve();
        },

        /**
         * Load Intake Metrics
         */
        async loadIntakeMetrics(mode = 'pressure') {
            // Simulate API call delay
            //await new Promise(resolve => setTimeout(resolve, 500));

			try {
				const response = await fetch(`/api/v0/metrics/intake`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						mode: mode
					})
				});

				console.warn(response);

				if (!response.ok) {
					throw new Error('Network response was not ok');
				}

				const data = await response.json();
				this.stats.clientActivity = data;
			} catch (error) {

			}

            // Mock data - replace with actual API calls
            this.stats.clientActivity = {
                profileViews: Math.floor(Math.random() * 100) + 50,
                caseNotes: Math.floor(Math.random() * 200) + 100,
                assessments: Math.floor(Math.random() * 50) + 25,
                documents: Math.floor(Math.random() * 300) + 150
            };
        },

        /**
         * Load user activity statistics
         * This would typically make an API call to get real-time data
         */
        async loadUserActivityStats() {
            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));

            // Mock data - replace with actual API calls
            this.stats.userActivity = {
                activeSessions: Math.floor(Math.random() * 20) + 5,
                loginsToday: Math.floor(Math.random() * 50) + 10,
                pageViews: Math.floor(Math.random() * 1000) + 500,
                idleExtensions: Math.floor(Math.random() * 30) + 5
            };
        },

        /**
         * Handle statistics loading errors
         */
        handleStatsError(viewKey) {
            // Set error state or default values
            switch (viewKey) {
                case 'client-activity':
                    this.stats.clientActivity = {
                        profileViews: '--',
                        caseNotes: '--',
                        assessments: '--',
                        documents: '--'
                    };
                    break;
                case 'user-activity':
                    this.stats.userActivity = {
                        activeSessions: '--',
                        loginsToday: '--',
                        pageViews: '--',
                        idleExtensions: '--'
                    };
                    break;
            }
        },

        /**
         * Refresh current view statistics
         */
        refreshStats() {
            this.loadStatsForView(this.currentView.key);
        },

        /**
         * Update date range and refresh stats
         */
        updateDateRange(range) {
            this.dateRange = range;
            this.loadStatsForView(this.currentView.key);
        },

        /**
         * Get formatted date range text
         */
        getDateRangeText() {
            const ranges = {
                week: 'This Week',
                month: 'This Month',
                quarter: 'This Quarter',
                year: 'This Year'
            };
            return ranges[this.dateRange] || 'Custom Range';
        }
    };
}
